import React, { useState, useEffect } from "react";
import { SERVER_URL, AUTH_STORAGE_KEY } from "../socket";

function ContactInfoPanel({
  profileUser,
  onClose,
  currentUser,
  groups,
  isInContacts = false,
  onAddContact,
  onChat,
  onMail,
  onCall,
}) {
  const [showFullAbout, setShowFullAbout] = useState(false);
  const [commonGroups,  setCommonGroups]  = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [showFullImage, setShowFullImage] = useState(false);
  const about    = profileUser?.aboutUs || "Hey there! I am using Kriva Chat";
  const isLong   = about.length > 80;
  const hasImage = !!profileUser?.profileImageUrl;
  const imgSrc   = hasImage ? `${SERVER_URL}${profileUser.profileImageUrl}` : null;
  const initial  = (profileUser?.fullName || profileUser?.userId || "U")[0].toUpperCase();
  const canChat  = typeof onChat === "function";
  const contactLabel = isInContacts ? "✅ Saved contact" : "➕ Not in contacts";
  const canMail  = typeof onMail === "function";
  const canCall  = typeof onCall === "function";

  useEffect(() => {
    if (!profileUser?.userId) return;
    const fetchCommonGroups = async () => {
      setLoadingGroups(true);
      try {
        const raw    = localStorage.getItem(AUTH_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        if (!parsed?.token) throw new Error("no token");
        const res = await fetch(
          `${SERVER_URL}/api/groups/common?with=${encodeURIComponent(profileUser.userId)}`,
          { headers: { Authorization: `Bearer ${parsed.token}` } }
        );
        if (!res.ok) throw new Error("api error");
        const data = await res.json();
        setCommonGroups(Array.isArray(data) ? data : []);
      } catch {
        if (groups?.length) {
          const contact = profileUser.userId?.toLowerCase();
          const common  = groups.filter(g =>
            (g.members || []).some(m =>
              (m.userId || m || "").toLowerCase() === contact
            )
          );
          setCommonGroups(common);
        } else {
          setCommonGroups([]);
        }
      } finally {
        setLoadingGroups(false);
      }
    };
    fetchCommonGroups();
  }, [profileUser?.userId]);

  if (!profileUser) return null;

  return (
    <>
      {/* ── Backdrop — LOWER than panel so panel clicks work ── */}
      <div
        onClick={onClose}
        style={{
          position:   "fixed",
          inset:      0,
          background: "rgba(0,0,0,0.35)",
          zIndex:     89999, // ✅ below panel (90000)
        }}
      />

      {/* ── Sliding Panel ── */}
      <div style={{
        position:      "fixed",
        top:           0,
        right:         0,
        width:         380,
        maxWidth:      "90vw",
        height:        "100vh",
        background:    "var(--bg-secondary, #1e293b)",
        boxShadow:     "-8px 0 32px rgba(0,0,0,0.5)",
        zIndex:        90000, // ✅ above backdrop
        display:       "flex",
        flexDirection: "column",
        overflowY:     "auto",
        animation:     "slideInRight 0.22s ease",
      }}>

        {/* Header */}
        <div style={{
          display:      "flex",
          alignItems:   "center",
          gap:          14,
          padding:      "16px 20px",
          background:   "var(--bg-primary, #0f172a)",
          borderBottom: "1px solid var(--border-primary, #1f2937)",
          position:     "sticky",
          top:          0,
          zIndex:       10,
        }}>
          <button
            onClick={onClose}
            style={{
              background:   "none",
              border:       "none",
              color:        "var(--text-secondary, #94a3b8)",
              fontSize:     20,
              cursor:       "pointer",
              padding:      4,
              borderRadius: 6,
            }}
          >
            ✕
          </button>
          <span style={{
            fontSize:   17,
            fontWeight: 600,
            color:      "var(--text-primary, #f1f5f9)",
          }}>
            Contact info
          </span>
        </div>
        {!isInContacts && typeof onAddContact === "function" && (
          <button
            type="button"
            onClick={onAddContact}
            style={{
              width: "100%",
              padding: "10px 12px",
              background: "linear-gradient(135deg, #1e40af, #3b82f6)",
              color: "#fff",
              border: "none",
              borderRadius: 12,
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              margin: "12px 20px 0",
            }}
          >
            ➕ Save to Contacts
          </button>
        )}
        {/* Avatar + Name */}
        <div style={{
          display:       "flex",
          flexDirection: "column",
          alignItems:    "center",
          padding:       "32px 20px 24px",
          borderBottom:  "1px solid var(--border-primary, #1f2937)",
        }}>
          {/* Clickable Avatar */}
          <div
            className="avatar-wrapper"
            onClick={() => { if (hasImage) setShowFullImage(true); }}
            style={{
              width:        120,
              height:       120,
              borderRadius: "50%",
              overflow:     "hidden",
              marginBottom: 16,
              border:       "3px solid #f57011",
              boxShadow:    "0 8px 24px rgba(245,112,17,0.3)",
              cursor:       hasImage ? "pointer" : "default",
              position:     "relative",
              flexShrink:   0,
            }}
          >
            {hasImage ? (
              <>
                <img
                  src={imgSrc}
                  alt="avatar"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                <div className="avatar-overlay">
                  <span style={{ fontSize: 26 }}>🔍</span>
                  <span style={{ fontSize: 11, fontWeight: 600 }}>View Photo</span>
                </div>
              </>
            ) : (
              <div style={{
                width:          "100%",
                height:         "100%",
                background:     "linear-gradient(135deg,#f57011,#ef4444)",
                display:        "flex",
                alignItems:     "center",
                justifyContent: "center",
                fontSize:       44,
                fontWeight:     700,
                color:          "#fff",
              }}>
                {initial}
              </div>
            )}
          </div>

          <h2 style={{
            margin:     0,
            fontSize:   22,
            fontWeight: 700,
            color:      "var(--text-primary, #f1f5f9)",
          }}>
            {profileUser.fullName || profileUser.userId}
          </h2>
          <p style={{
            margin:   "4px 0 0",
            fontSize: 13,
            color:    "var(--text-muted, #64748b)",
          }}>
            {profileUser.userId}
          </p>
          <span style={{
            marginTop: 8,
            padding: "6px 10px",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 700,
            color: isInContacts ? "#bbf7d0" : "#bfdbfe",
            background: isInContacts ? "rgba(34, 197, 94, 0.12)" : "rgba(59, 130, 246, 0.12)",
            border: isInContacts ? "1px solid rgba(34, 197, 94, 0.24)" : "1px solid rgba(59, 130, 246, 0.24)",
          }}>
            {contactLabel}
          </span>

          {(profileUser.profileNumberDisplay || profileUser.profileNumber) ? (
            <div style={{ fontSize: 12, color: "#f59e0b", marginTop: 4, fontWeight: 600 }}>
              {profileUser.profileNumberDisplay || profileUser.profileNumber}
            </div>
          ) : null}

          <div style={{
            display:       "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap:           10,
            width:         "100%",
            marginTop:     18,
          }}>
            {[
              {
                label: "Chat",
                icon: "💬",
                onClick: onChat,
                enabled: canChat,
              },
              {
                label: "Mail",
                icon: "✉️",
                onClick: onMail,
                enabled: canMail,
              },
              {
                label: "Call",
                icon: "📞",
                onClick: onCall,
                enabled: canCall,
              },
            ].map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={item.enabled ? item.onClick : undefined}
                disabled={!item.enabled}
                style={{
                  display:        "flex",
                  flexDirection:  "column",
                  alignItems:     "center",
                  justifyContent: "center",
                  gap:            6,
                  padding:        "12px 10px",
                  borderRadius:   16,
                  border:         "1px solid rgba(255,255,255,0.08)",
                  background:     item.enabled ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.02)",
                  color:          item.enabled ? "#f6724e" : "#8b94a6",
                  cursor:         item.enabled ? "pointer" : "not-allowed",
                  fontSize:       13,
                  fontWeight:     600,
                }}
              >
                <span style={{ fontSize: 20 }}>{item.icon}</span>
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* About */}
        <div style={{
          padding:      "20px",
          borderBottom: "1px solid var(--border-primary, #1f2937)",
        }}>
          <p style={{
            margin:        "0 0 8px",
            fontSize:      11,
            fontWeight:    700,
            color:         "#f57011",
            textTransform: "uppercase",
            letterSpacing: "0.6px",
          }}>
            About
          </p>
          <p style={{
            margin:     0,
            fontSize:   14,
            lineHeight: 1.6,
            color:      "var(--text-primary, #f1f5f9)",
          }}>
            {isLong && !showFullAbout ? (
              <>
                {about.slice(0, 80)}{"… "}
                <span
                  onClick={() => setShowFullAbout(true)}
                  style={{ color: "#22c55e", cursor: "pointer", fontWeight: 600 }}
                >
                  Read more
                </span>
              </>
            ) : about}
          </p>
        </div>
        {/* Business Details */}
<div style={{
  padding: "20px",
  borderBottom: "1px solid var(--border-primary, #1f2937)",
}}>

  <p style={{
    margin: "0 0 16px",
    fontSize: 11,
    fontWeight: 700,
    color: "#f57011",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
  }}>
    Business Profile
  </p>


  {/* Website */}
  {profileUser?.website && (
    <a
      href={profileUser.website}
      target="_blank"
      rel="noreferrer"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 14px",
        borderRadius: 12,
        background: "#0f172a",
        marginBottom: 10,
        textDecoration: "none",
      }}
    >
      <span style={{ fontSize: 20 }}>🌐</span>

      <div>
        <div style={{
          fontSize: 14,
          fontWeight: 600,
          color: "#f1f5f9",
        }}>
          Website
        </div>

        <div style={{
          fontSize: 12,
          color: "#64748b",
        }}>
          Open Website
        </div>
      </div>
    </a>
  )}

  {/* Social Links */}
  <div style={{
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    marginTop: 10,
  }}>

    {profileUser?.facebook && (
      <a
        href={profileUser.facebook}
        target="_blank"
        rel="noreferrer"
        style={{
          background: "#1877f2",
          color: "#fff",
          textDecoration: "none",
          borderRadius: 12,
          padding: "14px 10px",
          textAlign: "center",
          fontWeight: 700,
          fontSize: 13,
        }}
      >
        Facebook
      </a>
    )}

    {profileUser?.instagram && (
      <a
        href={profileUser.instagram}
        target="_blank"
        rel="noreferrer"
        style={{
          background: "linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)",
          color: "#fff",
          textDecoration: "none",
          borderRadius: 12,
          padding: "14px 10px",
          textAlign: "center",
          fontWeight: 700,
          fontSize: 13,
        }}
      >
        Instagram
      </a>
    )}

    {profileUser?.youtube && (
      <a
        href={profileUser.youtube}
        target="_blank"
        rel="noreferrer"
        style={{
          background: "#ff0000",
          color: "#fff",
          textDecoration: "none",
          borderRadius: 12,
          padding: "14px 10px",
          textAlign: "center",
          fontWeight: 700,
          fontSize: 13,
        }}
      >
        YouTube
      </a>
    )}

    {profileUser?.twitter && (
      <a
        href={profileUser.twitter}
        target="_blank"
        rel="noreferrer"
        style={{
          background: "#111827",
          color: "#fff",
          textDecoration: "none",
          borderRadius: 12,
          padding: "14px 10px",
          textAlign: "center",
          fontWeight: 700,
          fontSize: 13,
        }}
      >
        Twitter / X
      </a>
    )}

  </div>

  {/* Catalog */}
  {profileUser?.catalogUrl && (
    <a
      href={`${SERVER_URL}${profileUser.catalogUrl}`}
      target="_blank"
      rel="noreferrer"
      style={{
        marginTop: 18,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 16px",
        borderRadius: 14,
        background: "linear-gradient(135deg,#f57011,#ea580c)",
        textDecoration: "none",
        color: "#fff",
        fontWeight: 700,
      }}
    >

      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
      }}>
        <span style={{ fontSize: 24 }}>📕</span>

        <div>
          <div style={{ fontSize: 15 }}>
            Company Catalog
          </div>

          <div style={{
            fontSize: 12,
            opacity: 0.8,
            marginTop: 2,
          }}>
            View brochure / PDF
          </div>
        </div>
      </div>

      <span style={{ fontSize: 18 }}>
        →
      </span>

    </a>
  )}

</div>

        {/* Info rows */}
        <div style={{ borderBottom: "1px solid var(--border-primary, #1f2937)" }}>
          {[
            { icon: "📱", label: "Mobile",   value: profileUser.mobileNumber || "—" },
            { icon: "🎂", label: "Birthday", value: profileUser.dateOfBirth  || "—" },
            { icon: "⚧",  label: "Gender",   value: profileUser.gender       || "—" },
          ].map(row => (
            <div
              key={row.label}
              style={{
                display:      "flex",
                alignItems:   "center",
                gap:          16,
                padding:      "14px 20px",
                borderBottom: "1px solid rgba(255,255,255,0.04)",
              }}
            >
              <span style={{ fontSize: 20, flexShrink: 0 }}>{row.icon}</span>
              <div>
                <div style={{ fontSize: 15, color: "var(--text-primary, #f1f5f9)" }}>
                  {row.value}
                </div>
                <div style={{ fontSize: 12, color: "var(--text-muted, #64748b)", marginTop: 2 }}>
                  {row.label}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Groups in common */}
        <div style={{ padding: "20px 20px 24px" }}>
          <p style={{
            margin:     "0 0 16px",
            fontSize:   13,
            color:      "var(--text-muted, #64748b)",
            fontWeight: 600,
          }}>
            {loadingGroups
              ? "Loading groups…"
              : `${commonGroups.length} group${commonGroups.length !== 1 ? "s" : ""} in common`
            }
          </p>

          {/* Skeleton */}
          {loadingGroups && [1, 2, 3].map(i => (
            <div key={i} style={{
              display:    "flex",
              alignItems: "center",
              gap:        14,
              padding:    "10px 0",
            }}>
              <div style={{
                width:        48,
                height:       48,
                borderRadius: "50%",
                background:   "var(--bg-tertiary, #0f172a)",
                opacity:      0.5,
                flexShrink:   0,
                animation:    "pulse 1.4s ease infinite",
              }} />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{
                  height:     14,
                  borderRadius: 4,
                  background: "var(--bg-tertiary, #0f172a)",
                  opacity:    0.5,
                  width:      "60%",
                  animation:  "pulse 1.4s ease infinite",
                }} />
                <div style={{
                  height:     11,
                  borderRadius: 4,
                  background: "var(--bg-tertiary, #0f172a)",
                  opacity:    0.4,
                  width:      "80%",
                  animation:  "pulse 1.4s ease infinite",
                }} />
              </div>
            </div>
          ))}

          {/* Empty */}
          {!loadingGroups && commonGroups.length === 0 && (
            <div style={{
              padding:   "24px 0",
              textAlign: "center",
              color:     "var(--text-muted, #64748b)",
              fontSize:  13,
            }}>
              <span style={{ fontSize: 28, display: "block", marginBottom: 8 }}>👥</span>
              No groups in common
            </div>
          )}

          {/* Group list */}
          {!loadingGroups && commonGroups.map((group, idx) => {
            const members      = group.members || [];
            const nameList     = members.map(m => {
              const uid = (m.userId || m || "").toLowerCase();
              if (uid === currentUser?.toLowerCase()) return "You";
              return m.fullName || m.name || uid.split("@")[0] || uid;
            });
            const visibleNames = nameList.slice(0, 4).join(", ");
            const extra        = nameList.length > 4
              ? ` and ${nameList.length - 4} more`
              : "";

            return (
              <div
                key={group.id}
                style={{
                  display:      "flex",
                  alignItems:   "center",
                  gap:          14,
                  padding:      "12px 0",
                  borderBottom: idx < commonGroups.length - 1
                    ? "1px solid rgba(255,255,255,0.05)"
                    : "none",
                  cursor: "pointer",
                }}
              >
                <div style={{
                  width:          48,
                  height:         48,
                  borderRadius:   "50%",
                  overflow:       "hidden",
                  flexShrink:     0,
                  background:     "linear-gradient(135deg,#1d4ed8,#7c3aed)",
                  display:        "flex",
                  alignItems:     "center",
                  justifyContent: "center",
                }}>
                  {group.groupImage ? (
                    <img
                      src={`${SERVER_URL}${group.groupImage}`}
                      alt={group.name}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <span style={{ fontSize: 20, fontWeight: 700, color: "#fff" }}>
                      {group.name?.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize:     15,
                    fontWeight:   600,
                    color:        "var(--text-primary, #f1f5f9)",
                    whiteSpace:   "nowrap",
                    overflow:     "hidden",
                    textOverflow: "ellipsis",
                  }}>
                    {group.name}
                  </div>
                  <div style={{
                    fontSize:     12,
                    color:        "var(--text-muted, #64748b)",
                    marginTop:    3,
                    whiteSpace:   "nowrap",
                    overflow:     "hidden",
                    textOverflow: "ellipsis",
                  }}>
                    {visibleNames}{extra}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ══════════════════════════════════════════════
          Full-screen Image Viewer
          zIndex 999999 — above backdrop AND panel
      ══════════════════════════════════════════════ */}
      {showFullImage && hasImage && (
        <div
          onClick={() => setShowFullImage(false)}
          style={{
            position:       "fixed",
            inset:          0,
            background:     "rgba(0,0,0,0.97)",
            zIndex:         999999, // ✅ above everything
            display:        "flex",
            flexDirection:  "column",
            alignItems:     "center",
            justifyContent: "center",
          }}
        >
          {/* Top bar */}
          <div style={{
            position:       "absolute",
            top:            0,
            left:           0,
            right:          0,
            display:        "flex",
            alignItems:     "center",
            justifyContent: "space-between",
            padding:        "16px 20px",
            background:     "linear-gradient(180deg,rgba(0,0,0,0.75),transparent)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width:        36,
                height:       36,
                borderRadius: "50%",
                overflow:     "hidden",
                border:       "2px solid #f57011",
              }}>
                <img
                  src={imgSrc}
                  alt="avatar"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </div>
              <div>
                <div style={{ color: "#fff", fontSize: 14, fontWeight: 600 }}>
                  {profileUser.fullName || profileUser.userId}
                </div>
                <div style={{ color: "#94a3b8", fontSize: 11 }}>
                  Profile photo
                </div>
              </div>
            </div>

            {/* Close button */}
            <button
              onClick={(e) => { e.stopPropagation(); setShowFullImage(false); }}
              style={{
                background:     "rgba(255,255,255,0.12)",
                border:         "none",
                borderRadius:   "50%",
                width:          38,
                height:         38,
                color:          "#fff",
                fontSize:       18,
                cursor:         "pointer",
                display:        "flex",
                alignItems:     "center",
                justifyContent: "center",
              }}
            >
              ✕
            </button>
          </div>

          {/* Main Image — stopPropagation so clicking image doesn't close */}
          <img
            src={imgSrc}
            alt="profile"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth:     "90vw",
              maxHeight:    "78vh",
              borderRadius: 14,
              objectFit:    "contain",
              boxShadow:    "0 0 80px rgba(0,0,0,0.8)",
              userSelect:   "none",
              animation:    "fadeInScale 0.2s ease",
            }}
          />

          {/* Tap to close hint */}
          <div style={{
            position: "absolute",
            bottom:   80,
            color:    "rgba(255,255,255,0.3)",
            fontSize: 11,
          }}>
            Tap anywhere to close
          </div>
{/* Save Photo button */}
{/* <a
  href={imgSrc}
  download
  onClick={(e) => e.stopPropagation()}
  style={{
    position: "absolute",
    bottom: 24,
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "rgba(255,255,255,0.12)",
    color: "#fff",
    textDecoration: "none",
    padding: "10px 24px",
    borderRadius: 30,
    fontSize: 13,
    fontWeight: 600,
    backdropFilter: "blur(8px)",
    border: "1px solid rgba(255,255,255,0.18)",
    cursor: "pointer",
  }}
>
  ⬇ Save Photo
</a> */}
        </div>
      )}

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        @keyframes fadeInScale {
          from { transform: scale(0.88); opacity: 0; }
          to   { transform: scale(1);    opacity: 1; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50%       { opacity: 0.7; }
        }

        .avatar-wrapper .avatar-overlay {
          position:        absolute;
          inset:           0;
          background:      rgba(0,0,0,0.55);
          display:         flex;
          flex-direction:  column;
          align-items:     center;
          justify-content: center;
          opacity:         0;
          transition:      opacity 0.2s ease;
          color:           #fff;
          gap:             4px;
        }
        .avatar-wrapper:hover .avatar-overlay {
          opacity: 1;
        }
      `}</style>
    </>
  );
}

export default ContactInfoPanel;